// @ts-check
/** @typedef {import('../types/api').Collection} Collection */
/** @typedef {import('../types/api').PaginatedCollections} PaginatedCollections */
/** @typedef {import('../types/api').CollectionCount} CollectionCount */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const adminCollectionService = {
  /**
   * @param {Object} [params]
   * @returns {Promise<Collection[]>}
   */
  getCollections: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const data = await api.get(`/api/admin/collections/?${queryParams}`);
    assertArray(data, 'adminCollectionService.getCollections');
    return data;
  },

  /**
   * @param {Object} [params]
   * @returns {Promise<CollectionCount>}
   */
  getCollectionCount: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const data = await api.get(`/api/admin/collections/count?${queryParams}`);
    assertShape(data, ['count'], 'adminCollectionService.getCollectionCount');
    return data;
  },

  /**
   * @param {string} id
   * @returns {Promise<Collection>}
   */
  getCollection: async (id) => {
    const data = await api.get(`/api/admin/collections/${id}`);
    assertShape(data, ['id'], 'adminCollectionService.getCollection');
    return data;
  },

  /**
   * @param {Object} collectionData
   * @returns {Promise<Collection>}
   */
  createCollection: async (collectionData) => {
    return api.post('/api/admin/collections/', collectionData);
  },

  /**
   * @param {string} id
   * @param {Object} collectionData
   * @returns {Promise<Collection>}
   */
  updateCollection: async (id, collectionData) => {
    return api.put(`/api/admin/collections/${id}`, collectionData);
  },

  /**
   * @param {string} id
   * @returns {Promise<void>}
   */
  deleteCollection: async (id) => {
    return api.del(`/api/admin/collections/${id}`);
  },

  /**
   * @param {string} id
   * @param {boolean} isPublished
   * @returns {Promise<Collection>}
   */
  togglePublish: async (id, isPublished) => {
    return api.patch(`/api/admin/collections/${id}/publish?is_published=${isPublished}`, {});
  },

  /**
   * @param {{name: string, product_names?: string[], hero_image_url?: string}} facts
   * @returns {Promise<{description: string}>}
   */
  generateDescription: async (facts) => {
    return api.post('/api/admin/collections/generate-description', facts);
  },
};

export default adminCollectionService;
